/**
 * Air Situation Constants — PSP/CRE Tool v3.0
 *
 * Defines the friendly air situation spectrum for ENH-016.
 * Air situation is a theater-level environmental parameter that:
 *   1. Applies a casualty modifier (CAS availability, force protection)
 *   2. Overrides effective AD threat for MEDEVAC helicopter planning
 *
 * 2026-10-13: the MEDEVAC AD override (PERMISSIVE / HIGH / DENIED) is no longer used by the calculator; the helicopter availability is the
 * product of the air situation ceiling, the enemy air defense and the weather (constants/threatEffects.js). The doctrine references below are
 * NOT verified (Dr. Tatar 2026-10-13).
 *
 * Doctrinal basis:
 *   - NATO AJP-3.3 (Air Operations) — air superiority / parity / inferiority
 *   - FM 3-52 (Airspace Control)    — friendly/enemy air situation assessment
 *   - Ukraine 2022-2024 data        — contested airspace → +15-30% ground casualties
 *     (drone + MLRS targeting of assembly areas, reduced CAS availability)
 *
 * CASUALTY MODIFIER rationale:
 *   AIR_SUPERIORITY (0.85×): CAS available on demand → ~15% casualty reduction.
 *     Source: Dupuy TNDM air support modifiers; Gulf War data (30-60% reduction
 *     for full CAS). 0.85 is a conservative corps-level planning figure.
 *   PARITY (1.00×): Baseline. Neither side exploits airspace. No modifier.
 *   CONTESTED (1.15×): Reduced CAS, enemy ground attack aviation partially active.
 *     Ukraine 2022 pattern: +12-18% KIA/WIA in periods of contested airspace.
 *   ENEMY_AIR_SUPERIORITY (1.30×): No CAS, enemy air interdiction active.
 *     Historical: Korea 1950 (UN air denied) +25-35% casualty increase.
 *
 * MEDEVAC AD THREAT OVERRIDE:
 *   Overrides the Tab3 ad_threat for helicopter availability calculation only.
 *   null = use Tab3 ad_threat as configured (no override).
 *
 * @version 1.0.0
 * @date March 10, 2026
 * @author Dr. Tatar Radu MD
 */

// ============================================================================
// AIR SITUATION LEVELS
// ============================================================================

export const AIR_SITUATION_LEVELS = {
  AIR_SUPERIORITY: {
    label: 'Air Superiority (Friendly)',
    short_label: 'Air Sup',
    description: 'Friendly forces control the airspace. Battle casualties x0.85 (close air support); helicopter MEDEVAC ceiling 1.0.',
    casualty_modifier: 0.85,
    medevac_ad_threat_override: 'PERMISSIVE',  // AD threat effectively nullified
    color: 'success',
    planning_note: 'Effect in the model: battle casualty rate x0.85; helicopter MEDEVAC availability ceiling 1.0 (multiplied with the enemy air defense and the weather).'
  },
  PARITY: {
    label: 'Air Parity',
    short_label: 'Parity',
    description: 'Neither side controls the airspace. No change to battle casualties; helicopter MEDEVAC ceiling 0.8.',
    casualty_modifier: 1.0,
    medevac_ad_threat_override: null,  // No override — Tab3 ad_threat used as configured
    color: 'info',
    planning_note: 'Effect in the model: battle casualty rate x1.00 (default, no modification); helicopter MEDEVAC availability ceiling 0.8 (multiplied with the enemy air defense and the weather).'
  },
  CONTESTED: {
    label: 'Contested Airspace',
    short_label: 'Contested',
    description: 'Airspace disputed. Battle casualties x1.15 (less close air support); helicopter MEDEVAC ceiling 0.4.',
    casualty_modifier: 1.15,
    medevac_ad_threat_override: 'HIGH',  // Upgrade MEDEVAC AD threat to HIGH regardless of Tab3
    color: 'warning',
    planning_note: 'Effect in the model: battle casualty rate x1.15; helicopter MEDEVAC availability ceiling 0.4 (multiplied with the enemy air defense and the weather).'
  },
  ENEMY_AIR_SUPERIORITY: {
    label: 'Enemy Air Superiority',
    short_label: 'Enemy AS',
    description: 'Enemy controls the airspace. Battle casualties x1.30 (no close air support); helicopter MEDEVAC ceiling 0.1 (near zero).',
    casualty_modifier: 1.30,
    medevac_ad_threat_override: 'DENIED',  // MEDEVAC helicopters cannot operate
    color: 'error',
    planning_note: 'Effect in the model: battle casualty rate x1.30; helicopter MEDEVAC availability ceiling 0.1; ground evacuation is the main method.'
  }
};

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULT_AIR_SITUATION = 'PARITY';

export const DEFAULT_AIR_SITUATION_CONFIG = {
  level: DEFAULT_AIR_SITUATION
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get casualty modifier for a given air situation level.
 * Returns 1.0 (no effect) for unknown keys — backward compatible.
 * @param {string} level - Key from AIR_SITUATION_LEVELS
 * @returns {number} Casualty modifier (0.85 – 1.30)
 */
export function getAirSituationCasualtyModifier(level) {
  return AIR_SITUATION_LEVELS[level]?.casualty_modifier ?? 1.0;
}

/**
 * Get effective AD threat for MEDEVAC helicopter planning.
 * Returns null if the current level does not override Tab3 configuration.
 * @param {string} level - Key from AIR_SITUATION_LEVELS
 * @returns {string|null} MEDEVAC AD threat override, or null (no override)
 */
export function getAirSituationMEDEVACOverride(level) {
  return AIR_SITUATION_LEVELS[level]?.medevac_ad_threat_override ?? null;
}

/**
 * Validate an air situation config object.
 * @param {Object} config - { level: string }
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateAirSituationConfig(config) {
  const errors = [];
  if (!config?.level || !AIR_SITUATION_LEVELS[config.level]) {
    errors.push(`Unknown air situation level: "${config?.level}". Valid: ${Object.keys(AIR_SITUATION_LEVELS).join(', ')}`);
  }
  return { valid: errors.length === 0, errors };
}
